import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Button, Form, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faUsers,
  faBoxes,
  faCalendarAlt,
  faDownload,
  faRefresh,
  faShoppingCart,
  faDollarSign,
  faUserPlus,
  faCube,
  faCalendar,
  faUserFriends,
  faRepeat,
  faChartBar,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import MetricExplanationModal from "./MetricExplanationModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { es } from 'date-fns/locale';
import "../promotions/datepicker-custom.scss";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { createAuthenticatedAxiosInstance } from "../../api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import {
  fetchSalesReport,
  fetchBestSellingProducts,
  fetchNewUsersReport,
  fetchFrequentUsers,
  fetchDashboardSummary,
} from "../../api/auth";
import {
  ISalesReportResponse,
  IBestSellingProductsResponse,
  INewUsersReportResponse,
  IFrequentUsersResponse,
  IDashboardSummaryResponse,
  ReportPeriod,
  DashboardPeriod,
} from "../../interfaces/IReports";
import LoadingScreen from "../general/LoadingScreen";
import "./Reports.scss";

// Registrar los componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Reports: React.FC = () => {
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);

  // Estados para los datos de los reportes
  const [salesData, setSalesData] = useState<ISalesReportResponse | null>(null);
  const [bestSellingProducts, setBestSellingProducts] = useState<IBestSellingProductsResponse | null>(null);
  const [newUsersData, setNewUsersData] = useState<INewUsersReportResponse | null>(null);
  const [frequentUsers, setFrequentUsers] = useState<IFrequentUsersResponse | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<IDashboardSummaryResponse | null>(null);

  // Estados para filtros
  const [selectedPeriod, setSelectedPeriod] = useState<ReportPeriod>("monthly");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [productsLimit, setProductsLimit] = useState<number>(10);
  const [usersLimit, setUsersLimit] = useState<number>(10);

  // Función para calcular fechas predefinidas según el período
  const calculatePredefinedDates = (period: ReportPeriod) => {
    const today = new Date();
    const endDate = new Date(today);
    let startDate = new Date(today);

    switch (period) {
      case "daily":
        startDate.setDate(today.getDate() - 7); // 7 días antes
        break;
      case "weekly":
        startDate.setMonth(today.getMonth() - 1); // 1 mes antes
        break;
      case "monthly":
        startDate.setFullYear(today.getFullYear() - 1); // 1 año antes
        break;
      case "yearly":
        startDate.setFullYear(today.getFullYear() - 2); // 2 años antes
        break;
    }

    return { startDate, endDate };
  };

  // Función para actualizar fechas cuando cambia el período
  const handlePeriodChange = (period: ReportPeriod) => {
    setSelectedPeriod(period);
    const { startDate: newStartDate, endDate: newEndDate } = calculatePredefinedDates(period);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Referencias para DatePickers
  const startDateRef = useRef<DatePicker>(null);
  const endDateRef = useRef<DatePicker>(null);

  // Estados de carga y error
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Estados para manejar errores de cada reporte
  const [salesError, setSalesError] = useState<string | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [newUsersError, setNewUsersError] = useState<string | null>(null);
  const [frequentUsersError, setFrequentUsersError] = useState<string | null>(null);

  // Estados para el modal de explicación de métricas
  const [showMetricModal, setShowMetricModal] = useState<boolean>(false);
  const [selectedMetric, setSelectedMetric] = useState<string>("");

  // Función para cargar todos los reportes
  const loadAllReports = async () => {
    setLoading(true);
    setError("");
    
    // Limpiar errores anteriores
    setSalesError(null);
    setProductsError(null);
    setNewUsersError(null);
    setFrequentUsersError(null);
    
    try {
      // Formatear fechas para la API
      const formatDateForAPI = (date: Date | null): string | undefined => {
        if (!date) return undefined;
        return date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
      };

      const startDateStr = formatDateForAPI(startDate);
      const endDateStr = formatDateForAPI(endDate);

      // Cargar reporte de ventas
      try {
        const salesResponse = await fetchSalesReport(axiosInstance, selectedPeriod, startDateStr, endDateStr);
        setSalesData(salesResponse);
      } catch (err) {
        setSalesError("Error al cargar el reporte de ventas");
        setSalesData(null);
      }

      // Cargar productos más vendidos
      try {
        const productsResponse = await fetchBestSellingProducts(axiosInstance, productsLimit, startDateStr, endDateStr);
        setBestSellingProducts(productsResponse);
      } catch (err) {
        setProductsError("Error al cargar productos más vendidos");
        setBestSellingProducts(null);
      }

      // Cargar reporte de nuevos usuarios
      try {
        const usersResponse = await fetchNewUsersReport(axiosInstance, selectedPeriod, startDateStr, endDateStr);
        setNewUsersData(usersResponse);
      } catch (err) {
        setNewUsersError("Error al cargar reporte de nuevos usuarios");
        setNewUsersData(null);
      }

      // Cargar usuarios frecuentes
      try {
        const frequentResponse = await fetchFrequentUsers(axiosInstance, usersLimit, startDateStr, endDateStr);
        setFrequentUsers(frequentResponse);
      } catch (err) {
        setFrequentUsersError("Error al cargar usuarios frecuentes");
        setFrequentUsers(null);
      }

      // El resumen del dashboard se calculará a partir de los datos de las tablas
      // para asegurar consistencia con las fechas seleccionadas
      setDashboardSummary(null);

    } catch (err: any) {
      setError("Error general al cargar los reportes");
    } finally {
      setLoading(false);
    }
  };

  // Inicializar fechas al montar el componente
  useEffect(() => {
    const { startDate: initialStartDate, endDate: initialEndDate } = calculatePredefinedDates(selectedPeriod);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [selectedPeriod]);

  // Cargar reportes al montar el componente
  useEffect(() => {
    if (startDate && endDate) {
      loadAllReports();
    }
  }, []);

  // Recargar reportes cuando cambien los filtros
  useEffect(() => {
    if (startDate && endDate) {
      loadAllReports();
    }
  }, [selectedPeriod, startDate, endDate, productsLimit, usersLimit]);

  // Función para formatear moneda
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  // Función para formatear fecha
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("es-MX");
  };

  // Función para calcular el resumen del dashboard a partir de los datos de las tablas
  const calculateDashboardSummary = () => {
    if (!salesData || !newUsersData) return null;

    // Calcular métricas adicionales
    const conversionRate = (newUsersData.data.total_new_users || 0) > 0 
      ? ((salesData.data.total_orders || 0) / (newUsersData.data.total_new_users || 1) * 100).toFixed(1)
      : "0.0";

    const averageCustomerValue = (frequentUsers?.data?.summary?.total_users || 0) > 0
      ? salesData.data.total_revenue / (frequentUsers?.data?.summary?.total_users || 1)
      : 0;

    const purchaseFrequency = (frequentUsers?.data?.summary?.total_users || 0) > 0
      ? (frequentUsers?.data?.summary?.total_orders || 0) / (frequentUsers?.data?.summary?.total_users || 1)
      : 0;

    return {
      success: true,
      data: {
        period: selectedPeriod,
        total_orders: salesData.data.total_orders,
        total_revenue: salesData.data.total_revenue,
        new_users: newUsersData.data.total_new_users,
        active_products: bestSellingProducts?.data.length || 0,
        average_order_value: salesData.data.average_order_value,
        // Métricas adicionales
        conversion_rate: conversionRate,
        average_customer_value: averageCustomerValue,
        purchase_frequency: purchaseFrequency,
        top_selling_product: bestSellingProducts?.data[0] ? {
          name: bestSellingProducts.data[0].product_name,
          quantity_sold: bestSellingProducts.data[0].total_quantity_sold
        } : {
          name: "N/A",
          quantity_sold: 0
        }
      }
    };
  };

  // Calcular el resumen del dashboard
  const dashboardSummaryCalculated = calculateDashboardSummary();

  // Función para abrir el modal de explicación de métricas
  const handleMetricInfo = (metricType: string) => {
    setSelectedMetric(metricType);
    setShowMetricModal(true);
  };

  // Configuración para gráfica de ventas
  const salesChartData = {
    labels: salesData?.data.sales_data.map(item => item.period) || [],
    datasets: [
      {
        label: "Ventas ($)",
        data: salesData?.data.sales_data.map(item => item.revenue) || [],
        borderColor: "#997404",
        backgroundColor: "rgba(153, 116, 4, 0.1)",
        tension: 0.4,
      },
      {
        label: "Órdenes",
        data: salesData?.data.sales_data.map(item => item.orders_count) || [],
        borderColor: "#FBE584",
        backgroundColor: "rgba(251, 229, 132, 0.1)",
        tension: 0.4,
        yAxisID: "y1",
      },
    ],
  };

  // Configuración para gráfica de productos más vendidos
  const productsChartData = {
    labels: bestSellingProducts?.data.map(item => item.product_name) || [],
    datasets: [
      {
        label: "Cantidad Vendida",
        data: bestSellingProducts?.data.map(item => item.total_quantity_sold) || [],
        backgroundColor: [
          "#997404",
          "#FBE584",
          "#614900",
          "#C1C1C1",
          "#8B7355",
        ],
      },
    ],
  };

  // Configuración para gráfica de nuevos usuarios
  const newUsersChartData = {
    labels: newUsersData?.data.users_data.map(item => item.period) || [],
    datasets: [
      {
        label: "Nuevos Usuarios",
        data: newUsersData?.data.users_data.map(item => item.new_users_count) || [],
        backgroundColor: "#997404",
      },
    ],
  };

  // Debug: Mostrar información de nuevos usuarios
  console.log("🔍 Debug - Datos de nuevos usuarios:", {
    total_new_users: newUsersData?.data.total_new_users,
    users_data: newUsersData?.data.users_data,
    chart_labels: newUsersChartData.labels,
    chart_data: newUsersChartData.datasets[0].data,
  });

  // Configuración para gráfica de usuarios frecuentes
  const frequentUsersChartData = {
    labels: frequentUsers?.data.users.map(item => item.user_name) || [],
    datasets: [
      {
        label: "Total Gastado ($)",
        data: frequentUsers?.data.users.map(item => item.total_spent) || [],
        backgroundColor: "#FBE584",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Reportes de Ventas",
      },
    },
    scales: {
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
      },
      y1: {
        type: "linear" as const,
        display: true,
        position: "right" as const,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  if (loading) {
    return <LoadingScreen loadingText="Cargando reportes..." />;
  }

  return (
    <Container fluid className="reports-container">
      <Row className="mb-4">
        <Col>
          <h1 className="reports-title">
            <FontAwesomeIcon icon={faChartLine} className="me-3" />
            Reportes del Sistema
          </h1>
        </Col>
      </Row>

      {error && (
        <Alert variant="warning" className="mb-4">
          <strong>⚠️ Aviso:</strong> {error}
        </Alert>
      )}

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Período de Análisis</Form.Label>
                <Form.Select
                  value={selectedPeriod}
                  onChange={(e) => handlePeriodChange(e.target.value as ReportPeriod)}
                >
                  <option value="daily">Diario (Últimos 7 días)</option>
                  <option value="weekly">Semanal (Último mes)</option>
                  <option value="monthly">Mensual (Último año)</option>
                  <option value="yearly">Anual (Últimos 2 años)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Fecha Inicio</Form.Label>
                <div className="d-flex align-items-center">
                  <DatePicker
                    locale={es}
                    ref={startDateRef}
                    selected={startDate}
                    onChange={(date) => {
                      setStartDate(date);
                      if (endDate && date && date > endDate) {
                        setEndDate(null);
                      }
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="00/00/0000"
                    className="form-control date-picker-position"

                  />
                  <FontAwesomeIcon
                    icon={faCalendar}
                    className="ms-2"
                    onClick={() => startDateRef.current?.setOpen(true)}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Fecha Fin</Form.Label>
                <div className="d-flex align-items-center">
                  <DatePicker
                    locale={es}
                    ref={endDateRef}
                    selected={endDate}
                    onChange={setEndDate}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="00/00/0000"
                    className="form-control date-picker-position"
                    minDate={startDate || undefined}
                    maxDate={new Date()}
                  />
                  <FontAwesomeIcon
                    icon={faCalendar}
                    className="ms-2"
                    onClick={() => endDateRef.current?.setOpen(true)}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="primary"
                onClick={loadAllReports}
                className="w-100"
              >
                <FontAwesomeIcon icon={faRefresh} className="me-2" />
                Actualizar
              </Button>
            </Col>

          </Row>
        </Card.Body>
      </Card>

      {/* Resumen del Dashboard */}
      {(salesError || newUsersError) ? (
        <Alert variant="danger" className="mb-4">
          <strong>❌ Error en el resumen del dashboard:</strong> {salesError || newUsersError}
        </Alert>
      ) : dashboardSummaryCalculated ? (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center position-relative">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ cursor: "pointer", color: "#997404", fontSize: "0.9rem" }}
                  onClick={() => handleMetricInfo("total_orders")}
                />
                <FontAwesomeIcon icon={faShoppingCart} className="summary-icon-black" />
                <h3 className="summary-number-black">{dashboardSummaryCalculated.data.total_orders}</h3>
                <p className="summary-label-black">Órdenes Totales</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center position-relative">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ cursor: "pointer", color: "#997404", fontSize: "0.9rem" }}
                  onClick={() => handleMetricInfo("total_revenue")}
                />
                <FontAwesomeIcon icon={faDollarSign} className="summary-icon-black" />
                <h3 className="summary-number-black">{formatCurrency(dashboardSummaryCalculated.data.total_revenue)}</h3>
                <p className="summary-label-black">Ingresos Totales</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center position-relative">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ cursor: "pointer", color: "#997404", fontSize: "0.9rem" }}
                  onClick={() => handleMetricInfo("new_users")}
                />
                <FontAwesomeIcon icon={faUserPlus} className="summary-icon-black" />
                <h3 className="summary-number-black">{dashboardSummaryCalculated.data.new_users}</h3>
                <p className="summary-label-black">Nuevos Usuarios</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center position-relative">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ cursor: "pointer", color: "#997404", fontSize: "0.9rem" }}
                  onClick={() => handleMetricInfo("active_products")}
                />
                <FontAwesomeIcon icon={faCube} className="summary-icon-black" />
                <h3 className="summary-number-black">{dashboardSummaryCalculated.data.active_products}</h3>
                <p className="summary-label-black">Productos Activos</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center">
                <FontAwesomeIcon icon={faShoppingCart} className="summary-icon-black" />
                <h3 className="summary-number-black">-</h3>
                <p className="summary-label-black">Órdenes Totales</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center">
                <FontAwesomeIcon icon={faDollarSign} className="summary-icon-black" />
                <h3 className="summary-number-black">-</h3>
                <p className="summary-label-black">Ingresos Totales</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center">
                <FontAwesomeIcon icon={faUserPlus} className="summary-icon-black" />
                <h3 className="summary-number-black">-</h3>
                <p className="summary-label-black">Nuevos Usuarios</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center">
                <FontAwesomeIcon icon={faCube} className="summary-icon-black" />
                <h3 className="summary-number-black">-</h3>
                <p className="summary-label-black">Productos Activos</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Métricas Adicionales */}
      {dashboardSummaryCalculated && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center position-relative">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ cursor: "pointer", color: "#997404", fontSize: "0.9rem" }}
                  onClick={() => handleMetricInfo("conversion_rate")}
                />
                <FontAwesomeIcon icon={faChartLine} className="summary-icon-black" />
                <h3 className="summary-number-black">{dashboardSummaryCalculated.data.conversion_rate}%</h3>
                <p className="summary-label-black">Tasa de Conversión</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center position-relative">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ cursor: "pointer", color: "#997404", fontSize: "0.9rem" }}
                  onClick={() => handleMetricInfo("average_customer_value")}
                />
                <FontAwesomeIcon icon={faUserFriends} className="summary-icon-black" />
                <h3 className="summary-number-black">{formatCurrency(dashboardSummaryCalculated.data.average_customer_value)}</h3>
                <p className="summary-label-black">Valor por Cliente</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center position-relative">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ cursor: "pointer", color: "#997404", fontSize: "0.9rem" }}
                  onClick={() => handleMetricInfo("purchase_frequency")}
                />
                <FontAwesomeIcon icon={faRepeat} className="summary-icon-black" />
                <h3 className="summary-number-black">{dashboardSummaryCalculated.data.purchase_frequency.toFixed(1)}</h3>
                <p className="summary-label-black">Frecuencia de Compra</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="summary-card-white">
              <Card.Body className="text-center position-relative">
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  style={{ cursor: "pointer", color: "#997404", fontSize: "0.9rem" }}
                  onClick={() => handleMetricInfo("growth_rate")}
                />
                <FontAwesomeIcon icon={faChartBar} className="summary-icon-black" />
                <h3 className="summary-number-black">{newUsersData?.data.growth_rate || 0}%</h3>
                <p className="summary-label-black">Crecimiento</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Gráficas */}
      <Row>
        {/* Gráfica de Ventas */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>Reporte de Ventas</h5>
            </Card.Header>
            <Card.Body>
              {salesError ? (
                <Alert variant="danger">
                  <strong>❌ Error:</strong> {salesError}
                </Alert>
              ) : salesData ? (
                <>
                  <div className="mb-3">
                    <Row>
                      <Col md={4}>
                        <div className="stat-item">
                          <strong>Total Órdenes:</strong>
                          <span>{salesData.data.total_orders}</span>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="stat-item">
                          <strong>Ingresos Totales:</strong>
                          <span>{formatCurrency(salesData.data.total_revenue)}</span>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="stat-item">
                          <strong>Promedio por Orden:</strong>
                          <span>{formatCurrency(salesData.data.average_order_value)}</span>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <Line data={salesChartData} options={chartOptions} />
                </>
              ) : (
                <div className="text-center text-muted">
                  <p>Cargando reporte de ventas...</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfica de Productos Más Vendidos */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>Productos Más Vendidos</h5>
            </Card.Header>
            <Card.Body>
              {productsError ? (
                <Alert variant="danger">
                  <strong>❌ Error:</strong> {productsError}
                </Alert>
              ) : bestSellingProducts ? (
                <>
                  <div className="mb-3">
                    <Form.Group>
                      <Form.Label>Número de productos a mostrar:</Form.Label>
                      <Form.Select
                        value={productsLimit}
                        onChange={(e) => setProductsLimit(Number(e.target.value))}
                        style={{ width: "150px" }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <Bar data={productsChartData} />
                </>
              ) : (
                <div className="text-center text-muted">
                  <p>Cargando productos más vendidos...</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Gráfica de Nuevos Usuarios */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>Nuevos Usuarios</h5>
            </Card.Header>
            <Card.Body>
              {newUsersError ? (
                <Alert variant="danger">
                  <strong>❌ Error:</strong> {newUsersError}
                </Alert>
              ) : newUsersData ? (
                <>
                  <div className="mb-3">
                    <Row>
                      <Col md={4}>
                        <div className="stat-item">
                          <strong>Total Nuevos Usuarios:</strong>
                          <span>{newUsersData.data.total_new_users}</span>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="stat-item">
                          <strong>Tasa de Crecimiento:</strong>
                          <span>{newUsersData.data.growth_rate}%</span>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="stat-item">
                          <strong>Períodos:</strong>
                          <span>{newUsersData.data.users_data.length}</span>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <Bar data={newUsersChartData} />
                </>
              ) : (
                <div className="text-center text-muted">
                  <p>Cargando reporte de nuevos usuarios...</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfica de Usuarios Frecuentes */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>Usuarios Más Frecuentes</h5>
            </Card.Header>
            <Card.Body>
              {frequentUsersError ? (
                <Alert variant="danger">
                  <strong>❌ Error:</strong> {frequentUsersError}
                </Alert>
              ) : frequentUsers ? (
                <>
                  <div className="mb-3">
                    <Row>
                      <Col md={6}>
                        <div className="stat-item">
                          <strong>Total Usuarios:</strong>
                          <span>{frequentUsers.data.summary.total_users}</span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="stat-item">
                          <strong>Total Órdenes:</strong>
                          <span>{frequentUsers.data.summary.total_orders}</span>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <div className="stat-item">
                          <strong>Ingresos Totales:</strong>
                          <span>{formatCurrency(frequentUsers.data.summary.total_revenue)}</span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="stat-item">
                          <strong>Promedio por Orden:</strong>
                          <span>{formatCurrency(frequentUsers.data.summary.average_order_value)}</span>
                        </div>
                      </Col>
                    </Row>
                    <div className="mb-3">
                      <Form.Group>
                        <Form.Label>Número de usuarios a mostrar:</Form.Label>
                        <Form.Select
                          value={usersLimit}
                          onChange={(e) => setUsersLimit(Number(e.target.value))}
                          style={{ width: "150px" }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={15}>15</option>
                          <option value={20}>20</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </div>
                  <Bar data={frequentUsersChartData} />
                </>
              ) : (
                <div className="text-center text-muted">
                  <p>Cargando usuarios frecuentes...</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tablas de datos detallados */}
      <Row>
        {/* Tabla de Productos Más Vendidos */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>Detalle de Productos Más Vendidos</h5>
            </Card.Header>
            <Card.Body>
              {productsError ? (
                <Alert variant="danger">
                  <strong>❌ Error:</strong> {productsError}
                </Alert>
              ) : bestSellingProducts ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Marca</th>
                        <th>Cantidad</th>
                        <th>Ingresos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bestSellingProducts.data.map((product, index) => (
                        <tr key={index}>
                          <td>{product.product_name}</td>
                          <td>{product.brand}</td>
                          <td>{product.total_quantity_sold}</td>
                          <td>{formatCurrency(product.total_revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted">
                  <p>Cargando productos más vendidos...</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Tabla de Usuarios Frecuentes */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5>Detalle de Usuarios Frecuentes</h5>
            </Card.Header>
            <Card.Body>
              {frequentUsersError ? (
                <Alert variant="danger">
                  <strong>❌ Error:</strong> {frequentUsersError}
                </Alert>
              ) : frequentUsers ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Usuario</th>
                        <th>Email</th>
                        <th>Teléfono</th>
                        <th>Órdenes</th>
                        <th>Total Gastado</th>
                        <th>Última Compra</th>
                      </tr>
                    </thead>
                    <tbody>
                      {frequentUsers.data.users.map((user, index) => (
                        <tr key={index}>
                          <td>{user.user_name}</td>
                          <td>{user.user_email}</td>
                          <td>{user.user_phone}</td>
                          <td>{user.total_orders}</td>
                          <td>{formatCurrency(user.total_spent)}</td>
                          <td>{formatDate(user.last_order_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted">
                  <p>Cargando usuarios frecuentes...</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de explicación de métricas */}
      <MetricExplanationModal
        show={showMetricModal}
        onHide={() => setShowMetricModal(false)}
        metricType={selectedMetric}
      />
    </Container>
  );
};

export default Reports;
